import { useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  ChevronLeftSmallIcon,
  ChevronRightSmallIcon,
} from '@dynatrace/strato-icons';

import { useInternalTimeframeSelectorContext } from './hooks/useInternalTimeframeSelectorContext.js';
import { StepperButtonTooltip } from './StepperButtonTooltip.js';
import { formatStepperValues, getTimeShift } from './utils/stepper-helpers.js';
import {
  dateIsMinMax,
  isBiggerThan,
  isSmallerThan,
} from './utils/timeframe-selection.js';
import { type FormControlBaseProps } from '../../core/types/form-control-props.js';
import { type TimeframeV2 } from '../../core/types/time.js';
import { SelectedOverCalendarContext } from '../../forms/date-time-range-picker/contexts/SelectedOverCalendarContext.js';
import { removePrecision } from '../../forms/date-time-range-picker/helpers.js';

//#region Typings
export interface TimeframeStepperProps
  extends FormControlBaseProps<
    (timeframe: TimeframeV2, isMinOrMax: boolean) => void
  > {
  /** The value of the timeframe selector, containing the 'from' and 'to' timeframe details. */
  value: TimeframeV2;
  /** The direction the timeframe will be shifted to. */
  direction: 'backward' | 'forward';
}
//#endregion

function isStepperDisabled(
  direction: TimeframeStepperProps['direction'],
  value: TimeframeStepperProps['value'],
  min?: string,
  max?: string,
): boolean {
  if (direction === 'backward') {
    const nextStepIsValid =
      isSmallerThan(value.from.absoluteDate, min) ||
      dateIsMinMax(value.from, min, max);

    return nextStepIsValid;
  }

  if (direction === 'forward') {
    const nextStepIsValid =
      isBiggerThan(value.to.absoluteDate, max) ||
      dateIsMinMax(value.to, min, max);

    return nextStepIsValid;
  }

  return false;
}

/**
 * StepperButton component to shift the timeframe backward and forward.
 * @internal
 */
export const StepperButton = (props: TimeframeStepperProps) => {
  const {
    value,
    disabled,
    direction,
    'aria-disabled': ariaDisabledProp = false,
    onChange,
  }: TimeframeStepperProps = props;

  const [shiftValue, setShiftValue] = useState<TimeframeV2>(value);
  const {
    min: contextMin,
    max: contextMax,
    showError,
    readOnly,
    precision,
  } = useInternalTimeframeSelectorContext();
  const min = contextMin && removePrecision(contextMin, precision);
  const max = contextMax && removePrecision(contextMax, precision);

  const { selectedOverCalendar } = useContext(SelectedOverCalendarContext);

  const [isDisabled, setIsDisabled] = useState(
    isStepperDisabled(direction, value, min, max),
  );

  /** Handle value change. */
  useEffect(() => {
    setIsDisabled(isStepperDisabled(direction, value, min, max));

    const newShiftValue = getTimeShift(value, {
      direction,
      min,
      max,
      selectedOverCalendar,
      precision,
    });

    if (newShiftValue) {
      setShiftValue(newShiftValue);
    }
  }, [direction, value, min, max, selectedOverCalendar, isDisabled, precision]);

  /** Handle onClick. */
  const handleShift = useCallback(() => {
    const newShiftValue = getTimeShift(value, {
      direction,
      min,
      max,
      selectedOverCalendar,
      precision,
    });

    if (newShiftValue && !readOnly) {
      onChange?.(
        newShiftValue,
        direction === 'backward'
          ? dateIsMinMax(newShiftValue.from, min, max)
          : dateIsMinMax(newShiftValue.to, min, max),
      );
    }
  }, [
    value,
    direction,
    min,
    max,
    selectedOverCalendar,
    precision,
    readOnly,
    onChange,
  ]);

  const intl = useIntl();

  const formattedStepperValue = formatStepperValues(
    shiftValue.from,
    shiftValue.to,
    intl,
    direction,
    selectedOverCalendar,
  );

  return (
    <StepperButtonTooltip
      disabled={disabled || isDisabled}
      value={shiftValue}
      precision={precision}
      formattedStepperValue={formattedStepperValue}
    >
      <Button
        style={{ minWidth: 'fit-content' }}
        variant="emphasized"
        color={showError ? 'critical' : 'neutral'}
        disabled={disabled || isDisabled}
        onClick={handleShift}
        aria-disabled={ariaDisabledProp}
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        readOnly={readOnly}
        aria-label={formattedStepperValue}
      >
        {direction === 'backward' ? (
          <ChevronLeftSmallIcon />
        ) : (
          <ChevronRightSmallIcon />
        )}
      </Button>
    </StepperButtonTooltip>
  );
};

(StepperButton as typeof StepperButton & { displayName: string }).displayName =
  'TimeframeStepperButton';
